'use client'

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import type { HeatmapPayload } from '../svs-types'

interface HeatmapRendererProps {
  payload: HeatmapPayload
}

/**
 * Custom grid-based heatmap. Uses CSS background-color with opacity
 * scaled to the value range for a clean, accessible look.
 */
export function HeatmapRenderer({ payload }: HeatmapRendererProps) {
  const { xCategories, yCategories, cells } = payload.data
  const options = payload.options || {}

  // Build a lookup for quick cell access
  const cellMap = useMemo(() => {
    const map = new Map<string, { value: number; label?: string }>()
    for (const cell of cells) {
      map.set(`${cell.x},${cell.y}`, { value: cell.value, label: cell.label })
    }
    return map
  }, [cells])

  const allValues = cells.map(c => c.value)
  const minVal = Math.min(...allValues)
  const maxVal = Math.max(...allValues)
  const range = maxVal - minVal || 1

  function getCellColor(value: number): string {
    const normalized = (value - minVal) / range
    if (options.colorScale === 'diverging') {
      // Center is neutral, extremes are colored
      const midpoint = (maxVal + minVal) / 2
      if (value < midpoint) {
        const intensity = ((midpoint - value) / (midpoint - minVal))
        return `hsl(0, 84%, ${90 - intensity * 45}%)`   // red
      } else {
        const intensity = ((value - midpoint) / (maxVal - midpoint))
        return `hsl(142, 71%, ${90 - intensity * 45}%)`  // green
      }
    }
    // Sequential: light to saturated blue
    return `hsl(217, 91%, ${95 - normalized * 50}%)`
  }

  return (
    <div className="space-y-2">
      {/* Axis labels */}
      {options.xLabel && (
        <p className="text-center text-xs text-muted-foreground">{options.xLabel}</p>
      )}
      <div className="flex gap-1">
        {/* Y-axis labels */}
        <div className="flex flex-col gap-1 pr-1">
          {/* Spacer for corner */}
          <div className="h-7 w-12" />
          {yCategories.map((yLabel, yi) => (
            <div key={yi} className="flex h-8 w-12 items-center justify-end text-xs text-muted-foreground">
              {yLabel}
            </div>
          ))}
        </div>

        {/* Grid */}
        <div className="flex-1">
          {/* X-axis labels */}
          <div className="flex gap-1">
            {xCategories.map((xLabel, xi) => (
              <div key={xi} className="flex h-7 flex-1 items-end justify-center text-xs text-muted-foreground">
                {xLabel}
              </div>
            ))}
          </div>

          {/* Cells */}
          {yCategories.map((_yLabel, yi) => (
            <div key={yi} className="flex gap-1 mt-1">
              {xCategories.map((_xLabel, xi) => {
                const cell = cellMap.get(`${xi},${yi}`)
                return (
                  <div
                    key={xi}
                    className={cn(
                      'flex h-8 flex-1 items-center justify-center rounded text-xs font-medium transition-colors',
                      cell ? 'text-foreground' : 'bg-muted/30',
                    )}
                    style={cell ? { backgroundColor: getCellColor(cell.value) } : undefined}
                    title={cell ? `${cell.value}${cell.label ? ` (${cell.label})` : ''}` : 'No data'}
                  >
                    {options.showValues && cell && (
                      <span>{cell.value}</span>
                    )}
                    {cell?.label && !options.showValues && (
                      <span className="text-[10px]">{cell.label}</span>
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
      {options.yLabel && (
        <p className="text-center text-xs text-muted-foreground">{options.yLabel}</p>
      )}
    </div>
  )
}
